import { useRouter } from 'vue-router'

import { useAuthStore } from '../stores/authStore.js'
import { useScopeStore } from '../stores/scopeStore.js'
import { useTicketGroupStore } from '../stores/tickets/ticketGroupStore.js'
import { useTicketSettingsStore } from '../stores/tickets/ticketSettingsStore.js'
import { useTicketStore } from '../stores/tickets/ticketStore.js'

import cookieUtility from '../utils/cookie.utility.js'
import scopeUtility from '../utils/scope.utility.js'
import ticketGroupScopeUtility from '../utils/ticketGroupScope.utility.js'

import { useTicketMeta } from './tickets/useTicketMeta.js'
import { useTickets } from './tickets/useTickets.js'
import { useUser } from './useUser.js'

import { ROUTE_PATHS } from '../config/route.config.js'
import { goToV1Overview } from '../utils/v1Navigation.utility.js'

function isTicketPath(path) {
  return typeof path === 'string' && path.includes('/tehtavat/')
}

function apiFiltersFromSaved(savedFilters) {
  if (!savedFilters) return {}
  const apiFilters = { ...savedFilters }
  delete apiFilters.view
  return apiFilters
}

export function useSync() {
  const router = useRouter()
  const authStore = useAuthStore()
  const scopeStore = useScopeStore()
  const ticketStore = useTicketStore()
  const ticketSettingsStore = useTicketSettingsStore()
  const ticketGroupStore = useTicketGroupStore()
  const ticketMeta = useTicketMeta()
  const { refresh } = useUser()
  const tickets = useTickets()

  /**
   * Re-verify the session when the tab becomes visible, reconcile auth or
   * scope drift, and refresh stale ticket list caches. Skips the network call
   * when there is no local session and no CSRF cookie. Flushes ticket stores
   * when scope changes; stays on the current route unless auth/layout mode
   * actually changed.
   *
   * @returns {Promise<void>}
   */
  async function onVisible() {
    if (document.visibilityState !== 'visible') return

    const csrfToken = cookieUtility.get(`${import.meta.env.VITE_COOKIE_PREFIX}csrf_token`)
    const wasAuthenticated = Boolean(authStore.user)
    const hadSessionCookie = Boolean(csrfToken)
    const onGuestRoute = Boolean(router.currentRoute.value.meta.requiresGuest)
    /* HttpOnly cookies from v1 are not readable here — still verify on guest
       routes so a tab switch after v1 login can hydrate without a hard refresh. */
    if (!wasAuthenticated && !hadSessionCookie && !onGuestRoute) return

    const groupModeBefore = authStore.user?.groupMode ?? false
    const scopeBefore = scopeUtility.key(scopeStore.scope)
    const savedActiveFilters = ticketStore.active._meta?.filters
      ? { ...ticketStore.active._meta.filters }
      : null
    const savedArchivedFilters = ticketStore.archived._meta?.filters
      ? { ...ticketStore.archived._meta.filters }
      : null

    await refresh()
    const isAuthenticated = Boolean(authStore.user)
    const groupModeAfter = authStore.user?.groupMode ?? false
    const scopeAfter = scopeUtility.key(scopeStore.scope)

    if (wasAuthenticated && !isAuthenticated) {
      router.push(ROUTE_PATHS.login)
      return
    }

    if (!wasAuthenticated && isAuthenticated) {
      goToV1Overview({ group: groupModeAfter })
      return
    }

    if (!isAuthenticated) return

    if (groupModeBefore !== groupModeAfter) {
      ticketStore.reset()
      ticketSettingsStore.reset()
      ticketGroupStore.reset()
      goToV1Overview({ group: groupModeAfter })
      return
    }

    const scopeChanged = scopeAfter !== scopeBefore
    if (scopeChanged) {
      ticketStore.reset()
      ticketSettingsStore.reset()
      ticketGroupStore.reset()
    }

    if (!isTicketPath(router.currentRoute.value.path)) return

    const ticketGroupIds = ticketGroupScopeUtility.sanitizeIds(
      ticketGroupScopeUtility.parseIds(router.currentRoute.value.query),
      authStore.user?.ticketGroups,
    )
    const meta = ticketMeta.listMeta(ticketGroupIds)

    if (scopeChanged || !ticketStore.active._meta) {
      await Promise.all([
        tickets.forceLoadActiveTickets(apiFiltersFromSaved(savedActiveFilters), meta),
        tickets.forceLoadArchivedTickets(apiFiltersFromSaved(savedArchivedFilters), meta),
      ])
      return
    }

    await tickets.updateActiveTickets(meta)
    await tickets.updateArchivedTickets(meta)
  }

  return { onVisible }
}
